import React, { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useOrganization } from '@components/OrganizationBoundary';
import {
  toggleBillingModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { getAssetCount } from '@api/Asset';
import { useWebSocketSubscription } from '@hooks/useWebSocketClient';
import ApprovalEmptyStateView from '@components/Approval/ApprovalEmptyStateWrapper/ApprovalEmptyStateView';
import AssetsListModal from '@components/Approval/ApprovalEmptyStateWrapper/AssetsListModal';
import {
  ApprovalStateIcon,
  ApprovalEmptyStateIconsFreePlanIcon,
  ApprovalsEmptyIcon,
  UploadCampaignFilesIcon
} from '@icons';
import './ApprovalEmptyStateWrapper.less';

type ApprovalEmptyStateWrapperProps = {
  listLength: number;

  campaignId?: string;
  wholePage?: boolean;
  children?: ReactElement;
  refetchApprovalsData: () => void;
};

export default memo(function ApprovalEmptyStateWrapper({
  listLength,
  campaignId,
  wholePage,
  children,
  refetchApprovalsData
}: ApprovalEmptyStateWrapperProps) {
  const dispatch = useDispatch();
  const [assetsCount, setAssetsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAssetsModalOpen, setIsAssetsModalOpen] = useState<boolean>(false);

  const { currentOrganization } = useOrganization(true);
  const [currentWorkspace] = useCurrentWorkspace(true);
  const workspaceId = currentWorkspace.id;
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    if (currentWorkspace?.id) return;
    (async () => {
      setLoading(true);
      const assets = await getAssetCount({
        flatten: true,
        workspaceId: currentWorkspace.id,
        campaignId
      });
      setLoading(false);
      setAssetsCount(assets.data.count);
    })();
  }, [currentWorkspace?.id, campaignId]);

  useWebSocketSubscription(async (msg) => {
    if (msg.causedByMe && msg.payload.reason !== 'upload') return;
    const assets = await getAssetCount({
      flatten: true,
      workspaceId: currentWorkspace.id,
      campaignId
    });
    setAssetsCount(assets.data.count);
  }, 'asset-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.campaignId !== campaignId) return;
    const assets = await getAssetCount({
      flatten: true,
      workspaceId: currentWorkspace.id,
      campaignId
    });
    setAssetsCount(assets.data.count);
  }, 'asset-removed');

  const isFreePlan =
    (currentOrganization.entity?.planType ?? 'free') === 'free';

  const isContributor = useMemo(
    () => currentWorkspace.role === 'workspace://contributor',
    [currentWorkspace.role]
  );
  if (loading) return null;

  if (isFreePlan && !isContributor)
    return (
      <div
        className={classNames(
          'approval_empty',
          wholePage && 'approval_empty_whole_page'
        )}
      >
        <div className="approval_empty-container">
          {wholePage ? (
            <ApprovalEmptyStateView />
          ) : (
            <ApprovalEmptyStateIconsFreePlanIcon className="approval_empty-icon" />
          )}

          <div className="approval_empty-text">
            Upgrade to route creative media through approvals.
          </div>
          <Button
            type="primary"
            onClick={() => dispatch(toggleBillingModal({ visible: true }))}
          >
            Upgrade
          </Button>
        </div>
      </div>
    );

  if (!listLength && isContributor)
    return (
      <div
        className={classNames(
          'approval_empty',
          wholePage && 'approval_empty_whole_page'
        )}
      >
        <div className="approval_empty-container">
          {wholePage ? (
            <ApprovalEmptyStateView />
          ) : (
            <ApprovalStateIcon className="approval_empty-icon" />
          )}
          <div className="approval_empty-text" style={{ width: 290 }}>
            Creative media you&apos;ve been invited to review will appear here.
          </div>
        </div>
      </div>
    );
  if (!listLength && assetsCount <= 0)
    return (
      <div
        className={classNames(
          'approval_empty',
          wholePage && 'approval_empty_whole_page'
        )}
      >
        <div className="approval_empty-container">
          {wholePage ? (
            <ApprovalEmptyStateView />
          ) : (
            <UploadCampaignFilesIcon className="approval_empty-icon" />
          )}
          <div className="approval_empty-text">
            Upload a creative media to route through stakeholder approval.
          </div>
          <Button
            type="primary"
            onClick={() =>
              dispatch(
                toggleUploadAssetModal({
                  visible: true,
                  workspaceId,
                  campaignId
                })
              )
            }
          >
            Upload media
          </Button>
        </div>
      </div>
    );

  if (!listLength && assetsCount > 0)
    return (
      <>
        <div
          className={classNames(
            'approval_empty',
            wholePage && 'approval_empty_whole_page'
          )}
        >
          <div className="approval_empty-container">
            {wholePage && <ApprovalEmptyStateView />}
            {!wholePage && (
              <ApprovalsEmptyIcon className="approval_empty-icon" />
            )}
            <div className="approval_empty-text">
              Get your first creative media approved in a few easy steps.
            </div>
            <Button type="primary" onClick={() => setIsAssetsModalOpen(true)}>
              Start approval routing
            </Button>
          </div>
        </div>
        <AssetsListModal
          open={isAssetsModalOpen}
          workspaceId={workspaceId}
          assetsCount={assetsCount}
          campaignId={campaignId}
          refetchApprovalsData={refetchApprovalsData}
          onCloseModal={() => setIsAssetsModalOpen(false)}
        />
      </>
    );

  return children;
});
